<template>
  <MainLayout>
    <div class="past-estimates">
      <div class="past-estimates__header">
        <h1>{{ $t('pastEstimates.heading') }}</h1>
        <SearchInput v-model="searchPhrase" :placeholder="$t('pastEstimates.search')" />
      </div>
      <div class="past-estimates-mobile">
        <div
          class="estimate-mobile"
          v-for="({ name, date, address, id }) in filteredEstimates"
          :key="id"
        >
          <div class="estimate-mobile__first-line">
            <span class="estimate-mobile__data estimate-mobile__data--name">{{ name }}</span>
            <div class="estimate-mobile__buttons-wrapper--mobile">
              <img src="@/assets/icons/delete.svg" alt="delete" @click="confirmDelete(id)" />
              <router-link :to="{ name: 'editEstimate', params: { id } }">
                <img src="@/assets/icons/edit.svg" alt="edit" />
              </router-link>
            </div>
          </div>
          <span class="estimate-mobile__data estimate-mobile__data--date">{{ date }}</span>
          <span class="estimate-mobile__data estimate-mobile__data--address">{{ address }}</span>
        </div>
      </div>
      <table class="past-estimates-desktop">
        <tr
          class="estimate-desktop"
          v-for="({ name, date, address, id }) in filteredEstimates"
          :key="id"
        >
          <td class="estimate-desktop__data estimate-desktop__data--name">{{ name }}</td>
          <td class="estimate-desktop__data estimate-desktop__data--date">{{ date }}</td>
          <td class="estimate-desktop__data estimate-desktop__data--address">{{ address }}</td>
          <td class="estimate-desktop__buttons-wrapper">
            <button
              @click="confirmDelete(id)"
              class="estimate-desktop__button estimate-desktop__button--delete"
            >
              {{ $t('pastEstimates.delete') }}
            </button>
            <router-link :to="{ name: 'editEstimate', params: { id } }">
              <button class="estimate-desktop__button estimate-desktop__button--edit">
              {{ $t('pastEstimates.edit') }}
            </button>
            </router-link>
          </td>
        </tr>
      </table>
    </div>
  </MainLayout>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import { Input as AntInput } from 'ant-design-vue';

import MainLayout from '@/components/layouts/MainLayout';

const { mapActions, mapGetters } = createNamespacedHelpers('estimate');

export default {
  name: 'PastEstimates',
  components: { MainLayout, SearchInput: AntInput.Search },
  data() {
    return {
      searchPhrase: '',
    };
  },
  computed: {
    ...mapGetters({ estimates: 'allEstimatesStubs' }),
    filteredEstimates() {
      if (this.searchPhrase) {
        // this should be handled by the backend
        return this.estimates.filter(({ name }) => new RegExp(this.searchPhrase, 'i').test(name));
      } else {
        return this.estimates;
      }
    },
  },
  methods: {
    ...mapActions({
      getAllEstimations: 'getAll',
      deleteEstimate: 'deleteEstimate',
    }),
    confirmDelete(estimateId) {
      this.$modals.confirm({
        header: this.$t('pastEstimates.modal.header'),
        content: this.$t('pastEstimates.modal.content'),
        confirmText: this.$t('pastEstimates.modal.buttons.confirm'),
        onConfirm: () => this.handleDeleteConfirm(estimateId),
      });
    },
    async handleDeleteConfirm(estimateId) {
      try {
        await this.deleteEstimate({ id: estimateId });
      } catch (error) {
        this.$notifications.error(error.message || this.$t('pastEstimates.deleteFailMsg'));
      }
    },
  },
  created() {
    this.getAllEstimations();
  },
};
</script>

<style lang="scss">
  .past-estimates {
    box-shadow: 0 2px 83px 0 rgba(34, 48, 87, .07);

    &__header {
      background-color: $stepperBackground;
      border-radius: 18px 18px 0 0;
      height: 124px;
      padding: 20px;

      @include md-up {
        align-items: center;
        display: flex;
        justify-content: space-between;
        padding: 20px 40px;
      }

      h1 {
        font-size: 16px;
        font-weight: 600;
        letter-spacing: .21px;
        margin-bottom: 14px;

        @include md-up {
          font-size: 18px;
          margin: 0;
        }
      }
    }

    .past-estimates-mobile {
      background-color: $white;
      border-radius: 0 0 18px 18px;

      @include md-up {
        display: none;
      }

      .estimate-mobile {
        padding: 20px;

        &:not(:last-child) {
          border-bottom: 1px solid $grayLight;
        }

        &__data {
          color: $primary;
          display: block;
          flex: 0 0 auto;
          font-size: 14px;

          &--name {
            flex: 1;
            font-weight: 600;
            margin-right: 12px;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          &--date {
            margin: 10px 0;
          }
        }

        &__first-line {
          align-items: center;
          display: flex;
          justify-content: space-between;

          img {
            &:first-child {
              margin-right: 12px;
            }
          }
        }
      }
    }

    .past-estimates-desktop {
      background-color: $white;
      border-radius: 0 0 18px 18px;
      display: none;
      width: 100%;

      @include md-up {
        display: table;
      }

      .estimate-desktop {
        height: 80px;
        width: 100%;

        &:not(:last-child) {
          border-bottom: 1px solid $grayLight;
        }

        &__data-wrapper {
          align-items: center;
          border: 1px dashed green;
          display: flex;
          flex-grow: 1;
        }

        &__data {
          color: $primary;
          font-size: 14px;
          padding: 20px;

          &--name {
            font-weight: 600;
            padding-left: 40px;
          }

          &--date {
            width: 140px;
          }
        }

        &__buttons-wrapper {
          box-sizing: content-box;
          padding-right: 20px;
          width: 224px;
        }

        &__button {
          align-items: center;
          border-radius: 12px;
          cursor: pointer;
          display: inline-flex;
          font-size: 14px;
          font-weight: 600;
          height: 54px;
          justify-content: center;
          letter-spacing: .19px;
          width: 90px;

          &--delete {
            background-color: $white;
            border: 1px solid $grayLight;
            color: $red;
            margin-right: 24px;
          }

          &--edit {
            background-color: rgba($blueDarker, .15);
            border: none;
            color: $blueDarker;
          }
        }
      }
    }

    .ant-input-search {
      @include md-up {
        max-width: 330px;
      }

      .ant-input {
        background-color: transparent;
        border-radius: 12px;
        border: 1px solid $grayLight;
        color: $primary;
        font-size: 13px;
        font-weight: 500;
        height: 50px;
        letter-spacing: .3px;
        padding: 0 20px;

        &:hover {
          border-color: $accent;
        }

        &::placeholder {
          color: $gray;
          opacity: .41;
        }
      }
    }
  }
</style>
